import React, { useState, useEffect } from "react";
import { Badge, Button, Card, Form, Modal, Table } from "react-bootstrap";
import { FaTrashAlt, FaPencilAlt, FaEye, FaFileExcel, FaFilePdf, FaFile } from "react-icons/fa";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import * as XLSX from "xlsx";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function RuleBook({ user, setFetchRules, fetchRules }) {
  const [rules, setRules] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteRuleId, setDeleteRuleId] = useState(null);
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [fetch, setFetch] = useState(0);
  const [showAckModal, setShowAckModal] = useState(false);
  const [ackList, setAckList] = useState([]);
  const [showFileModal, setShowFileModal] = useState(false);
  const [fileUrl, setFileUrl] = useState("");
  const [selectedRule, setSelectedRule] = useState('');
  const [fileType, setFileType] = useState('');
  const [excelData, setExcelData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeSheet, setActiveSheet] = useState(0);
  const isAdmin = user.role === 4;

  // Fetch all rules from DB
  useEffect(() => {
    axios.get(`${API_URL}/api/rule-book`).then((res) => {
      const data = res.data.map(r => ({
        ...r,
        ac_employee: r.ac_employee
          ? r.ac_employee.split(",").map(id => id.trim())
          : []
      }));
      setRules(data);
    });
  }, [fetch]);

  const handleAdd = () => {
    setEditIndex(null);
    setTitle("");
    setLink("");
    setShowModal(true);
  };

  const handleEdit = (idx) => {
    const rule = rules[idx];
    const fullPath = rule.link || "";
    const fileName = typeof fullPath === 'string' ? fullPath.split("/").pop() : "";

    setEditIndex(rule.id);
    setTitle(rule.title);
    setLink(fileName);
    setShowModal(true);
  };

  const confirmDelete = (id) => {
    setDeleteRuleId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deleteRuleId) return;
    await axios.delete(`${API_URL}/api/rule-book/${deleteRuleId}`);
    setRules(rules.filter((rule) => rule.id !== deleteRuleId));
    setShowDeleteModal(false);
    toast.success("Rule deleted successfully");
  };

  const handleSave = async () => {
    if (!title.trim() || !(link instanceof File)) return;

    const formData = new FormData();
    formData.append("title", title.trim());
    if (link) formData.append("file", link);

    try {
      let res;
      if (editIndex === null) {
        res = await axios.post(`${API_URL}/api/rule-book`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const newRule = {
          ...res.data,
          ac_employee: res.data.ac_employee
            ? res.data.ac_employee.split(",").map(id => id.trim())
            : []
        };
        setRules([newRule, ...rules]);
        toast.success("New rule added successfully");
      } else {
        res = await axios.put(`${API_URL}/api/rule-book/${editIndex}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const updatedRule = {
          ...res.data,
          ac_employee: res.data.ac_employee
            ? res.data.ac_employee.split(",").map(id => id.trim())
            : []
        };
        setRules(rules.map((r) => (r.id === editIndex ? updatedRule : r)));
        toast.success("Rule updated successfully");
      }
      setFetchRules((prev) => prev + 1);
      setFetch(fetch + 1);
      setShowModal(false);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save rule");
    }
  };

  const handleAcknowledge = async (ruleId, checked) => {
    const rule = rules.find(r => r.id === ruleId);

    // ⛔ Already acknowledged → do nothing
    if (rule?.ac_employee?.includes(user?.name)) return;

    try {
      const res = await axios.put(`${API_URL}/api/rule-book/acknowledge`, {
        ruleId,
        employeeId: user.name,
        isChecked: true, // always true
      });

      setRules(rules.map(r =>
        r.id === ruleId ? { ...r, ac_employee: res.data.ac_employee } : r
      ));

      toast.success("Acknowledged successfully");
    } catch (error) {
      toast.error("Failed to update acknowledgement");
    }
  };


  const parseExcelFile = async (fileUrl) => {
    setIsLoading(true);
    try {
      // Use axios instead of fetch to download the file
      const response = await axios.get(fileUrl, {
        responseType: 'arraybuffer'
      });

      const arrayBuffer = response.data;
      const data = new Uint8Array(arrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });

      const parsedData = {
        sheetNames: workbook.SheetNames,
        sheets: {}
      };

      workbook.SheetNames.forEach(sheetName => {
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        parsedData.sheets[sheetName] = jsonData;
      });

      setExcelData(parsedData);
      setActiveSheet(0);
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      // toast.error('Failed to parse Excel file');
      setExcelData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const openFileModal = async (ruleId) => {
    const rule = rules.find(r => r.id === ruleId);
    if (rule && rule.link) {
      const fileUrl = `${API_URL}${rule.link}`;
      let fileExtension = '';

      // Safely get file extension
      if (typeof rule.link === 'string') {
        fileExtension = rule.link.split('.').pop().toLowerCase();
      }

      setFileUrl(fileUrl);
      setSelectedRule(rule);

      if (fileExtension === 'pdf') {
        setFileType('pdf');
        setExcelData(null);
        setShowFileModal(true);
      } else if (['xlsx', 'xls', 'csv'].includes(fileExtension)) {
        setFileType('excel');
        setShowFileModal(true);
        // Parse Excel file after modal is shown
        await parseExcelFile(fileUrl);
      } else {
        setFileType('other');
        setExcelData(null);
        setShowFileModal(true);
      }
    } else {
      toast.error("File not available yet. Please try again in a moment.");
    }
  };

  useEffect(() => {
    if (showFileModal && selectedRule?.id) {
      const updated = rules.find(r => r.id === selectedRule.id);
      if (updated) setSelectedRule(updated);
    }
  }, [rules]);

  const renderExcelPreview = () => {
    if (isLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center h-100">
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading Excel file...</p>
          </div>
        </div>
      );
    }

    if (!excelData) {
      return (
        <div className="d-flex flex-column align-items-center justify-content-center h-100">
          <FaFileExcel size={80} color="#1D6F42" className="mb-3" />
          <h5>Excel Document</h5>
          <p className="text-muted text-center mb-3">
            Failed to load Excel preview. Please download the file to view it.
          </p>
          <Button
            variant="success"
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
          >
            <FaFileExcel className="me-2" />
            Download Excel File
          </Button>
        </div>
      );
    }

    const currentSheetName = excelData.sheetNames[activeSheet];
    const sheetData = excelData.sheets[currentSheetName] || [];

    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Sheet Navigation */}
        {excelData.sheetNames.length > 1 && (
          <div className="mb-3 p-2 bg-light border rounded">
            <label className="form-label mb-1"><strong>Sheets:</strong></label>
            <div className="d-flex flex-wrap gap-2">
              {excelData.sheetNames.map((sheetName, index) => (
                <Button
                  key={sheetName}
                  size="sm"
                  variant={activeSheet === index ? "primary" : "outline-primary"}
                  onClick={() => setActiveSheet(index)}
                >
                  {sheetName}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Excel Data Table */}
        <div style={{ flex: 1, overflow: "auto" }}>
          {sheetData.length > 0 ? (
            <Table striped bordered hover responsive size="sm">
              <tbody>
                {sheetData.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {Array.isArray(row) ? (
                      row.map((cell, cellIndex) => (
                        <td key={cellIndex} style={{
                          minWidth: "100px",
                          backgroundColor: rowIndex === 0 ? "#f8f9fa" : "transparent",
                          fontWeight: rowIndex === 0 ? "bold" : "normal"
                        }}>
                          {cell !== null && cell !== undefined ? String(cell) : ""}
                        </td>
                      ))
                    ) : (
                      <td>{String(row)}</td>
                    )}
                    {/* Fill empty cells if needed */}
                    {Array.isArray(row) && row.length < Math.max(...sheetData.map(r => r.length)) &&
                      Array.from({ length: Math.max(...sheetData.map(r => r.length)) - row.length }).map((_, index) => (
                        <td key={`empty-${index}`}></td>
                      ))
                    }
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <p className="text-muted">No data found in this sheet</p>
            </div>
          )}
        </div>

        {/* File Info */}
        <div className="mt-2 p-2 bg-light border rounded">
          <small className="text-muted">
            <strong>Sheet:</strong> {currentSheetName} |
            <strong> Rows:</strong> {sheetData.length} |
            <strong> Columns:</strong> {sheetData[0] ? sheetData[0].length : 0}
          </small>
        </div>
      </div>
    );
  };

  // Safe function to get file icon
  const getFileIcon = (filePath) => {
    if (!filePath || typeof filePath !== 'string') {
      return <FaFile className="me-2" />;
    }

    try {
      const extension = filePath.split('.').pop().toLowerCase();
      switch (extension) {
        case 'pdf':
          return <FaFilePdf className="me-2" style={{ color: '#dc3545' }} />;
        case 'xlsx':
        case 'xls':
        case 'csv':
          return <FaFileExcel className="me-2" style={{ color: '#1D6F42' }} />;
        default:
          return <FaFile className="me-2" />;
      }
    } catch (error) {
      console.error('Error getting file icon:', error);
      return <FaFile className="me-2" />;
    }
  };

  // Render file preview based on file type
  const renderFilePreview = () => {
    switch (fileType) {
      case 'pdf':
        return (
          <iframe
            src={fileUrl}
            title="PDF Viewer"
            style={{ width: "100%", height: "70vh", border: "none", display: "block" }}
            onError={() => toast.error("Failed to load PDF")}
          />
        );

      case 'excel':
        return renderExcelPreview();

      case 'other':
        return (
          <div className="d-flex flex-column align-items-center justify-content-center h-100">
            <FaFile size={80} color="#6c757d" className="mb-3" />
            <h5>Document</h5>
            <p className="text-muted mb-3">This file type cannot be previewed in the browser.</p>
            <Button
              variant="primary"
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              download
            >
              <FaFile className="me-2" />
              Download File
            </Button>
          </div>
        );

      default:
        return (
          <div className="text-muted d-flex justify-content-center align-items-center h-100">
            <div>
              <div>No file available</div>
              <small className="text-muted">The document may still be processing...</small>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="p-4" style={{ backgroundColor: "#f5f7fa" }}>
      <h3>Company Rule Book</h3>
      <p>
        <strong>Total Rules:</strong> {rules.length}
      </p>
      <ToastContainer />
      {isAdmin && (
        <Button className="mb-3" onClick={handleAdd}>
          + Add New Rule
        </Button>
      )}

      {rules.length === 0 && <div className="text-muted">No rules defined yet.</div>}

      {rules.map((rule, idx) => {
        return (
          <Card key={rule.id || idx} className="mb-3 shadow-sm">
            <Card.Body className="d-flex flex-row flex-md-row justify-content-between align-items-start">
              <div className="flex-grow-1 text-break me-2">
                <Card.Title as="h6">
                  {idx + 1}.{" "}
                  <span
                    variant="link"
                    onClick={() => openFileModal(rule.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <b>{rule.title}</b>
                    {!rule.ac_employee?.includes(user?.name) && (
                      <Badge
                        bg="danger"
                        pill
                        className="position-absolute top-0 start-80 border border-light rounded-circle"
                      >
                        New
                      </Badge>
                    )}
                  </span>
                </Card.Title>
              </div>

              {isAdmin && (
                <div className="d-flex gap-3 mt-2 mt-md-0">
                  <FaEye
                    size={20}
                    color="blue"
                    title="View Acknowledged Employee"
                    role="button"
                    onClick={() => {
                      setAckList(rule.ac_employee || []);
                      setShowAckModal(true);
                    }}
                  />
                  <FaPencilAlt
                    size={20}
                    color="green"
                    role="button"
                    title="Update Rule"
                    onClick={() => handleEdit(idx)}
                  />
                  <FaTrashAlt
                    size={20}
                    color="red"
                    role="button"
                    title="Delete Rule"
                    onClick={() => confirmDelete(rule.id)}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        );
      })}

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header>
          <Modal.Title>{editIndex === null ? "Add Rule" : "Edit Rule"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Title</strong>
              </Form.Label>
              <Form.Control
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter rule title"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Rule File</strong>
              </Form.Label>
              <Form.Control
                type="file"
                onChange={(e) => setLink(e.target.files[0])}
                accept=".pdf,.xlsx,.xls,.csv,.doc,.docx"
              />
              <Form.Text className="text-muted">
                Supported formats: PDF, Excel (.xlsx, .xls, .csv), Word (.doc, .docx)
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!title.trim() || !link}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this rule?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Acknowledged Employees Modal */}
      <Modal show={showAckModal} onHide={() => setShowAckModal(false)} centered>
        <Modal.Header>
          <Modal.Title>Acknowledged Employees</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "300px", overflowY: "auto" }}>
          {ackList.length === 0 ? (
            <div className="text-muted">No acknowledgements yet.</div>
          ) : (
            <ul>
              {ackList.map((id, i) => (
                <li key={i}>{id}</li>
              ))}
            </ul>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAckModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* File Preview Modal */}
      <Modal show={showFileModal} onHide={() => setShowFileModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {getFileIcon(selectedRule?.link)}
            {selectedRule?.title || 'Document Preview'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "70vh", padding: 0, overflow: "hidden" }}>
          {renderFilePreview()}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Form.Check
            type="checkbox"
            id={`ack-${selectedRule?.id}`}
            label="I acknowledge"
            checked={selectedRule?.ac_employee?.includes(user?.name)}
            disabled={selectedRule?.ac_employee?.includes(user?.name)} // ✅ disable once acknowledged
            onChange={(e) =>
              handleAcknowledge(selectedRule?.id, e.target.checked)
            }
            className="mt-2 cursor-pointer"
          />
          <Button
            variant="outline-primary"
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
          >
            <FaFile className="me-2" />
            Download
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}